import React, { useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * Controlled confirmation dialog — replaces `window.confirm` which Chrome
 * can silently disable ("Prevent this page from creating additional dialogs").
 *
 * Usage:
 *   const [confirm, setConfirm] = useState(null);
 *   ...
 *   setConfirm({ title: "Eliminare?", message: "...", onConfirm: async () => { ... } });
 *   ...
 *   <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
 */
export default function ConfirmDialog({ state, onClose }) {
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, onClose]);

  if (!state) return null;
  const {
    title = "Confermi?",
    message,
    confirmLabel = "Conferma",
    cancelLabel = "Annulla",
    variant = "danger",
    onConfirm,
  } = state;

  const confirmBtn =
    variant === "danger"
      ? "bg-red-600 hover:bg-red-700 text-white"
      : "bg-slate-900 hover:bg-slate-800 text-white";

  const handleConfirm = async () => {
    try {
      await onConfirm?.();
    } finally {
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center p-4 pt-16 overflow-y-auto"
      onClick={onClose}
      data-testid="confirm-dialog"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 flex items-start gap-3">
          {variant === "danger" && (
            <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="text-red-600" size={20} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-slate-900 text-base">{title}</div>
            {message && <div className="text-sm text-slate-600 mt-1">{message}</div>}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0"
            data-testid="confirm-dialog-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-200"
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${confirmBtn}`}
            data-testid="confirm-dialog-confirm"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
